import { Menu, User, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { useNavigate } from 'react-router-dom';
import { LanguageSwitcher } from './language-switcher';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';

interface HeaderProps {
  title?: string;
  onMenuClick?: () => void;
}

export default function Header({ title, onMenuClick }: HeaderProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="flex items-center justify-between px-4 py-3 bg-[#0F1524] border-b border-[#1A2235] sticky top-0 z-10">
      <div className="flex items-center gap-3">
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg bg-[#1A2235] hover:bg-[#253045] transition-colors md:hidden"
            aria-label={t('header.menu')}
          >
            <Menu size={24} className="text-[#00C2CB]" />
          </button>
        )}
        {title && <h2 className="text-xl font-medium text-[#00C2CB] hidden md:block">{title}</h2>}
      </div>

      {/* User profile section */}
      <div className="flex items-center gap-3">
        <LanguageSwitcher />
        <span className="text-white font-medium">{user?.name || t('header.defaultUser')}</span>
        <div className="relative" ref={dropdownRef}>
          <div className="flex items-center gap-1 cursor-pointer" onClick={handleProfileClick}>
            <button
              className="w-9 h-9 rounded-full overflow-hidden bg-[#1A2234] flex items-center justify-center"
              style={{
                filter: 'drop-shadow(0px 0.77px 5.07px rgba(0,0,0,0.25))'
              }}
              aria-label={t('header.profile')}
            >
              <User size={18} className="text-white" />
            </button>
            {isDropdownOpen ? (
              <ChevronUp className="h-4 w-4 text-white" />
            ) : (
              <ChevronDown className="h-4 w-4 text-white" />
            )}
          </div>
          
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-[#1A2235] border border-[#253045] rounded-md shadow-lg py-1 z-20">
              <button
                onClick={() => handleNavigation('/profile')}
                className="block px-4 py-2 text-sm text-white hover:bg-[#253045] w-full text-left"
              >
                {t('profile.personal_info', 'Personal Information')}
              </button>
              <button
                onClick={() => handleNavigation('/memory')}
                className="block px-4 py-2 text-sm text-white hover:bg-[#253045] w-full text-left"
              >
                {t('memory.title', 'Memory')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
